import React, { useState, useEffect, useRef } from 'react';
import { FaRProject, FaCode, FaCopy, FaDownload, FaCog, FaDesktop, FaPlay, FaExternalLinkAlt } from 'react-icons/fa';
import './CodeContextMenu.css';
import { 
  detectApplications, 
  setApplicationAvailability, 
  formatCodeForApplication, 
  getFileExtension, 
  getMimeType, 
  downloadCode, 
  copyToClipboard 
} from '../utils/applicationDetector';

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8000`;

const CodeContextMenu = ({ 
  code, 
  language, 
  isVisible, 
  position, 
  onClose, 
  onCopy, 
  onDownload 
}) => {
  const [availableApps, setAvailableApps] = useState({
    r: false,
    rstudio: false,
    sas: false
  });
  const [installedIDEs, setInstalledIDEs] = useState({});
  const [isExecuting, setIsExecuting] = useState(false);
  const menuRef = useRef(null);

  // Check for available applications and IDEs
  useEffect(() => {
    checkAvailableApplications();
    detectInstalledIDEs();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose]);

  const checkAvailableApplications = async () => {
    try {
      const apps = await detectApplications();
      setAvailableApps(apps);
    } catch (error) {
      console.error('Error detecting applications:', error);
      // Fallback to showing all options
      setAvailableApps({
        r: true,
        rstudio: true,
        sas: true
      });
    }
  };

  const handleOpenInR = () => {
    const rCode = formatCodeForApplication(code, language, 'r');
    openInApplication('r', rCode);
    onClose();
  };

  const handleOpenInRStudio = () => {
    const rCode = formatCodeForApplication(code, language, 'rstudio');
    openInApplication('rstudio', rCode);
    onClose();
  };

  const handleOpenInSAS = () => {
    const sasCode = formatCodeForApplication(code, language, 'sas');
    openInApplication('sas', sasCode);
    onClose();
  };

  // New IDE Integration Functions
  const detectInstalledIDEs = async () => {
    try {
      const response = await fetch(`${BACKEND_BASE}/api/ide/detect`);
      const data = await response.json();
      if (data.success) {
        setInstalledIDEs(data.installed_ides);
        return data.installed_ides;
      }
      return {};
    } catch (error) {
      console.error('Error detecting IDEs:', error);
      return {};
    }
  };

  const handleOpenInIDE = async (ideType) => {
    try {
      const response = await fetch(`${BACKEND_BASE}/api/ide/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code,
          language: language,
          ide_type: ideType
        })
      });
      
      const data = await response.json();
      if (data.success) {
        showNotification(`Code opened in ${ideType.toUpperCase()}!`, 'success');
      } else {
        showNotification(data.message || `Failed to open in ${ideType.toUpperCase()}`, 'error');
      }
    } catch (error) {
      showNotification(`Error opening in ${ideType.toUpperCase()}: ${error.message}`, 'error');
    }
    onClose();
  };

  const handleRunCode = async () => {
    setIsExecuting(true);
    try {
      const response = await fetch(`${BACKEND_BASE}/api/ide/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code,
          language: language,
          timeout: 30
        })
      });
      
      const data = await response.json();
      showExecutionResult(data);
    } catch (error) {
      showNotification(`Error executing code: ${error.message}`, 'error');
    } finally {
      setIsExecuting(false);
    }
    onClose();
  };

  const handleRunAndOpen = async (ideType) => {
    setIsExecuting(true);
    try {
      const response = await fetch(`${BACKEND_BASE}/api/ide/run_and_open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code,
          language: language,
          ide_type: ideType
        })
      });
      
      const data = await response.json();
      if (data.success) {
        showExecutionResult(data.execution);
        showNotification(`Code executed and opened in ${ideType.toUpperCase()}!`, 'success');
      } else {
        showNotification(data.message || `Failed to run and open in ${ideType.toUpperCase()}`, 'error');
      }
    } catch (error) {
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsExecuting(false);
    }
    onClose();
  };

  const showExecutionResult = (result) => {
    // Cache busting: Updated to remove error section - v2
    const modal = document.createElement('div');
    modal.className = 'code-instructions-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0; left: 0; width: 100vw; height: 100vh;
      background: rgba(0,0,0,0.8); z-index: 10000;
      display: flex; align-items: center; justify-content: center; padding: 20px;
    `;

    const statusColor = result.success ? '#28a745' : '#dc3545';
    const statusText = result.success ? 'Success' : `Failed (code ${result.returncode})`;

    // Only show output, no errors
    const outputContent = result.stdout ? result.stdout.replace(/</g,'&lt;') : '(no output)';
    
    modal.innerHTML = `
      <div style="background: var(--background-color, #fff); color: var(--text-color, #333); border-radius: 12px; width: 90%; max-width: 800px; max-height: 85vh; overflow: auto; padding: 30px; box-shadow: 0 10px 30px rgba(0,0,0,0.3);">
        <h2 style="margin-top:0; color: var(--text-color, #333); font-size: 24px;">🚀 Code Execution Results</h2>
        <div style="margin: 15px 0; padding: 12px 16px; background: ${statusColor}; color: #fff; border-radius: 8px; display: inline-block; font-weight: bold;">
          ${statusText} (${result.execution_time?.toFixed(2)}s)
        </div>
        
        <div style="margin-top: 20px;">
          <div>
            <div style="font-weight: bold; margin-bottom: 8px; color: var(--text-color, #333); font-size: 16px;">📤 Output</div>
            <pre style="background: var(--assistant-message-background, #f8f9fa); border: 1px solid var(--border-color, #e9ecef); border-radius: 8px; padding: 15px; white-space: pre-wrap; font-family: 'Courier New', monospace; font-size: 14px; max-height: 300px; overflow-y: auto; color: var(--text-color, #333);">${outputContent}</pre>
          </div>
        </div>
        
        <div style="text-align: right; margin-top: 25px;">
          <button onclick="this.closest('.code-instructions-modal').remove()" style="padding: 12px 24px; border: 1px solid var(--border-color, #ddd); border-radius: 8px; background: var(--button-background, #f8f9fa); color: var(--text-color, #333); cursor: pointer; font-size: 14px; font-weight: bold;">Close</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove();
    });
  };

  const openInApplication = async (appType, formattedCode) => {
    try {
      const filename = `code_${Date.now()}.${getFileExtension(appType)}`;
      const mimeType = getMimeType(appType);
      
      // First, copy the code to clipboard
      const clipboardSuccess = await copyToClipboard(formattedCode);
      
      if (clipboardSuccess) {
        // Download the file
        const downloadSuccess = downloadCode(formattedCode, filename, mimeType);
        
        if (downloadSuccess) {
          // Try to open the application directly
          const directOpenSuccess = await tryDirectApplicationOpen(appType, filename, formattedCode);
          
          if (directOpenSuccess) {
            showNotification(`Code opened in ${appType.toUpperCase()}!`, 'success');
          } else {
            // Fallback to instructions modal
            showApplicationInstructions(appType, filename, formattedCode);
          }
          
          // Mark this application as available for future use
          setApplicationAvailability(appType, true);
        } else {
          showNotification('Code copied to clipboard, but failed to download file', 'warning');
        }
      } else {
        // Fallback to just downloading
        const downloadSuccess = downloadCode(formattedCode, filename, mimeType);
        if (downloadSuccess) {
          showNotification(`Code saved as ${filename}. You can now open it in ${appType.toUpperCase()}.`, 'info');
        } else {
          showNotification('Error: Could not save code file', 'error');
        }
      }
      
    } catch (error) {
      console.error('Error opening in application:', error);
      showNotification('Error: Could not open code in application', 'error');
    }
  };

  const tryDirectApplicationOpen = async (appType, filename, formattedCode) => {
    try {
      console.log(`Attempting to open ${appType} with filename: ${filename}`);
      
      // Method 1: Try to create and download a file with proper MIME type
      // This should trigger the file association on macOS
      try {
        console.log('Trying file creation with MIME type...');
        const blob = new Blob([formattedCode], { type: getApplicationMimeType(appType) });
        const url = URL.createObjectURL(blob);
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Wait a moment for the download to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Try to open the downloaded file using file protocol
        try {
          console.log('Attempting to open downloaded file...');
          // Try different file URL formats
          const fileUrls = [
            `file://${filename}`,
            `file:///${filename}`,
            `file://${window.location.origin}/${filename}`
          ];
          
          for (const fileUrl of fileUrls) {
            try {
              window.open(fileUrl, '_blank');
              console.log(`File URL ${fileUrl} opened successfully`);
              URL.revokeObjectURL(url);
              return true;
            } catch (e) {
              console.log(`File URL ${fileUrl} failed:`, e);
            }
          }
        } catch (e) {
          console.log('Could not open file directly:', e);
        }
        
        URL.revokeObjectURL(url);
        console.log('File download completed, but direct opening failed');
        return false;
      } catch (e) {
        console.log('File creation method failed:', e);
      }
      
      // Method 2: Try custom URL schemes (limited support on macOS)
      const urlSchemes = {
        r: ['r://'],
        rstudio: ['rstudio://'],
        sas: ['sas://']
      };
      
      const schemes = urlSchemes[appType] || [];
      for (const scheme of schemes) {
        try {
          console.log(`Trying URL scheme: ${scheme}`);
          const newWindow = window.open(scheme, '_blank');
          if (newWindow) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            console.log(`URL scheme ${scheme} opened successfully`);
            return true;
          }
        } catch (e) {
          console.log(`URL scheme ${scheme} failed:`, e);
        }
      }
      
      console.log('All direct opening methods failed');
      return false;
    } catch (error) {
      console.error('Error in direct application open:', error);
      return false;
    }
  };

  const getApplicationMimeType = (appType) => {
    const mimeTypes = {
      r: 'application/x-r',
      rstudio: 'application/x-r',
      sas: 'application/x-sas'
    };
    return mimeTypes[appType] || 'text/plain';
  };

  const showApplicationInstructions = (appType, filename, formattedCode) => {
    const instructions = getApplicationInstructions(appType, filename);
    
    // Create a modal with instructions
    const modal = document.createElement('div');
    modal.className = 'code-instructions-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    `;
    
    modal.innerHTML = `
      <div style="
        background: var(--background-color, white);
        color: var(--text-color, #333);
        border-radius: 12px;
        padding: 30px;
        max-width: 600px;
        width: 100%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        font-family: Arial, sans-serif;
      ">
        <h2 style="margin: 0 0 20px 0; color: var(--text-color, #333); font-size: 24px;">
          🚀 Open Code in ${appType.toUpperCase()}
        </h2>
        <div style="margin-bottom: 20px; line-height: 1.6; color: var(--text-color, #555);">
          ${instructions}
        </div>
        <div style="
          background: var(--assistant-message-background, #f8f9fa);
          border: 1px solid var(--border-color, #e9ecef);
          border-radius: 8px;
          padding: 15px;
          margin: 20px 0;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          color: var(--text-color, #333);
          white-space: pre-wrap;
          max-height: 200px;
          overflow-y: auto;
        ">${formattedCode.substring(0, 500)}${formattedCode.length > 500 ? '\n...' : ''}</div>
        <div style="display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px;">
          <button onclick="this.closest('.code-instructions-modal').remove()" style="
            padding: 10px 20px;
            border: 1px solid var(--border-color, #ddd);
            border-radius: 6px;
            background: var(--button-background, #f8f9fa);
            color: var(--text-color, #333);
            cursor: pointer;
            font-size: 14px;
          ">Close</button>
          <button onclick="navigator.clipboard.writeText('${formattedCode.replace(/'/g, "\\'")}'); this.textContent='Copied!'; setTimeout(() => this.textContent='Copy Code', 2000)" style="
            padding: 10px 20px;
            border: none;
            border-radius: 6px;
            background: #007bff;
            color: white;
            cursor: pointer;
            font-size: 14px;
          ">Copy Code</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Close modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  };

  const getApplicationInstructions = (appType, filename) => {
    switch (appType) {
      case 'r':
        return `
          <strong>✅ Code copied to clipboard!</strong><br><br>
          <strong>To open in R Console:</strong><br>
          1. Open R Console on your computer<br>
          2. Press <kbd>Ctrl+V</kbd> (Windows) or <kbd>Cmd+V</kbd> (Mac) to paste the code<br>
          3. Press <kbd>Enter</kbd> to run the code<br><br>
          <strong>Alternative:</strong> The code has also been saved as <code>${filename}</code> which you can open in R.
        `;
      case 'rstudio':
        return `
          <strong>✅ Code copied to clipboard!</strong><br><br>
          <strong>To open in RStudio:</strong><br>
          1. Open RStudio on your computer<br>
          2. Create a new R Script (Ctrl+Shift+N)<br>
          3. Press <kbd>Ctrl+V</kbd> (Windows) or <kbd>Cmd+V</kbd> (Mac) to paste the code<br>
          4. Save the script and run it<br><br>
          <strong>Alternative:</strong> The code has also been saved as <code>${filename}</code> which you can open directly in RStudio.
        `;
      case 'sas':
        return `
          <strong>✅ Code copied to clipboard!</strong><br><br>
          <strong>To open in SAS Studio:</strong><br>
          1. Open SAS Studio on your computer<br>
          2. Create a new program<br>
          3. Press <kbd>Ctrl+V</kbd> (Windows) or <kbd>Cmd+V</kbd> (Mac) to paste the code<br>
          4. Run the program<br><br>
          <strong>Alternative:</strong> The code has also been saved as <code>${filename}</code> which you can open directly in SAS.
        `;
      default:
        return `
          <strong>✅ Code copied to clipboard!</strong><br><br>
          The code has been copied to your clipboard and saved as <code>${filename}</code>.
        `;
    }
  };

  const showNotification = (message, type = 'success') => {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = `code-notification ${type}`;
    notification.textContent = message;
    
    const colors = {
      success: '#28a745',
      error: '#dc3545',
      warning: '#ffc107',
      info: '#17a2b8'
    };
    
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${colors[type] || colors.success};
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      animation: notificationSlideIn 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };

  const handleCopyCode = async () => {
    if (onCopy) {
      onCopy(code);
    } else {
      const success = await copyToClipboard(code);
      if (success) {
        showNotification('Code copied to clipboard!');
      } else {
        showNotification('Failed to copy code', 'error');
      }
    }
    onClose();
  };

  const handleDownloadCode = () => {
    if (onDownload) {
      onDownload(code, language);
    } else {
      const filename = `code_${Date.now()}.${language || 'txt'}`;
      const success = downloadCode(code, filename, 'text/plain');
      if (success) {
        showNotification('Code downloaded!');
      } else {
        showNotification('Failed to download code', 'error');
      }
    }
    onClose();
  };

  const handleOpenSettings = () => {
    // Open a simple settings dialog
    const settings = prompt(
      `Configure available applications (comma-separated):\n\n` +
      `Available options: r, rstudio, sas\n` +
      `Current: ${Object.keys(availableApps).filter(app => availableApps[app]).join(', ')}\n\n` +
      `Enter the applications you have installed:`,
      Object.keys(availableApps).filter(app => availableApps[app]).join(', ')
    );
    
    if (settings !== null) {
      const selectedApps = settings.split(',').map(app => app.trim().toLowerCase());
      const newApps = {
        r: selectedApps.includes('r'),
        rstudio: selectedApps.includes('rstudio'),
        sas: selectedApps.includes('sas')
      };
      
      setAvailableApps(newApps);
      
      // Save to localStorage
      Object.keys(newApps).forEach(app => {
        setApplicationAvailability(app, newApps[app]);
      });
      
      showNotification('Settings updated!');
    }
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div
      ref={menuRef}
      className="code-context-menu"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 1000
      }}
    >
      <div className="context-menu-header">
        <FaCode className="context-menu-icon" />
        <span>Code Actions</span>
      </div>
      
      <div className="context-menu-divider"></div>
      
      <div className="context-menu-section">
        <div className="context-menu-label">Open With</div>
        
        {availableApps.r && (
          <button 
            className="context-menu-item"
            onClick={handleOpenInR}
            title="Open in R"
          >
            <FaRProject className="context-menu-item-icon" />
            <span>R Console</span>
          </button>
        )}
        
        {availableApps.rstudio && (
          <button 
            className="context-menu-item"
            onClick={handleOpenInRStudio}
            title="Open in RStudio"
          >
            <FaDesktop className="context-menu-item-icon" />
            <span>RStudio</span>
          </button>
        )}
        
        {availableApps.sas && (
          <button 
            className="context-menu-item"
            onClick={handleOpenInSAS}
            title="Open in SAS"
          >
            <FaCode className="context-menu-item-icon" />
            <span>SAS Studio</span>
          </button>
        )}
      </div>
      
      {/* New IDE Integration Section */}
      {Object.keys(installedIDEs).length > 0 && (
        <>
          <div className="context-menu-divider"></div>
          
          <div className="context-menu-section">
            <div className="context-menu-label">🚀 Direct IDE Integration</div>
            
            {installedIDEs.rstudio && (
              <>
                <button 
                  className="context-menu-item"
                  onClick={() => handleOpenInIDE('rstudio')}
                  title="Open directly in RStudio"
                >
                  <FaExternalLinkAlt className="context-menu-item-icon" />
                  <span>Open in RStudio</span>
                </button>
                
                <button 
                  className="context-menu-item"
                  onClick={() => handleRunAndOpen('rstudio')}
                  title="Run code and open in RStudio"
                  disabled={isExecuting}
                >
                  <FaPlay className="context-menu-item-icon" />
                  <span>Run & Open RStudio</span>
                </button>
              </>
            )}
            
            {installedIDEs.r && (
              <>
                <button 
                  className="context-menu-item"
                  onClick={() => handleOpenInIDE('r')}
                  title="Open directly in R Console"
                >
                  <FaExternalLinkAlt className="context-menu-item-icon" />
                  <span>Open in R Console</span>
                </button>
                
                <button 
                  className="context-menu-item"
                  onClick={() => handleRunAndOpen('r')}
                  title="Run code and open in R Console"
                  disabled={isExecuting}
                >
                  <FaPlay className="context-menu-item-icon" />
                  <span>Run & Open R</span>
                </button>
              </>
            )}
            
            {installedIDEs.sas && (
              <>
                <button 
                  className="context-menu-item"
                  onClick={() => handleOpenInIDE('sas')}
                  title="Open directly in SAS"
                >
                  <FaExternalLinkAlt className="context-menu-item-icon" />
                  <span>Open in SAS</span>
                </button>
                
                <button 
                  className="context-menu-item"
                  onClick={() => handleRunAndOpen('sas')}
                  title="Run code and open in SAS"
                  disabled={isExecuting}
                >
                  <FaPlay className="context-menu-item-icon" />
                  <span>Run & Open SAS</span>
                </button>
              </>
            )}
          </div>
        </>
      )}
      
      <div className="context-menu-divider"></div>
      
      <div className="context-menu-section">
        <div className="context-menu-label">Actions</div>
        
        <button 
          className="context-menu-item"
          onClick={handleCopyCode}
          title="Copy code to clipboard"
        >
          <FaCopy className="context-menu-item-icon" />
          <span>Copy Code</span>
        </button>
        
        <button 
          className="context-menu-item"
          onClick={handleDownloadCode}
          title="Download code as file"
        >
          <FaDownload className="context-menu-item-icon" />
          <span>Download</span>
        </button>
        
        <button 
          className="context-menu-item"
          onClick={handleRunCode}
          title="Execute code and show results"
          disabled={isExecuting}
        >
          <FaPlay className="context-menu-item-icon" />
          <span>{isExecuting ? 'Running...' : 'Run Code'}</span>
        </button>
      </div>
      
      <div className="context-menu-divider"></div>
      
      <div className="context-menu-section">
        <div className="context-menu-info">
          <small>Language: {language || 'Unknown'}</small>
        </div>
        <button 
          className="context-menu-item context-menu-settings"
          onClick={handleOpenSettings}
          title="Configure available applications"
        >
          <FaCog className="context-menu-item-icon" />
          <span>Settings</span>
        </button>
      </div>
    </div>
  );
};

export default CodeContextMenu;
